import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import platformService from '../../services/platformService';

export default function DealerList() {
  const navigate = useNavigate();

  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');

  // Create Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Fetch dealers
  const fetchDealers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await platformService.getDealers({
        page: page + 1,
        limit: rowsPerPage,
        search
      });

      if (response.success) {
        setDealers(response.data.dealers);
        setTotal(response.data.pagination.total);
      }
    } catch (err) {
      setError('Failed to load dealers');
      console.error('Fetch dealers error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealers();
  }, [page, rowsPerPage, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleCreateDialogOpen = () => {
    setCreateForm({
      name: '',
      email: '',
      password: ''
    });
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateFormChange = (field) => (e) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleCreateDealer = async () => {
    // Validation
    if (!createForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!createForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!createForm.password || createForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await platformService.createDealer({
        name: createForm.name,
        email: createForm.email,
        password: createForm.password
      });

      if (response.success) {
        toast.success('Dealer created successfully');
        handleCreateDialogClose();
        fetchDealers();
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create dealer';
      toast.error(message);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Dealers
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <IconButton onClick={fetchDealers} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateDialogOpen} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Add Dealer
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by name or email..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 400, sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                <TableCell>Tenants</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'none', md: 'table-cell' } }}>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : dealers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No dealers found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                dealers.map((dealer) => (
                  <TableRow
                    key={dealer.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/dealers/${dealer.id}`)}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{dealer.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{dealer.email || '-'}</TableCell>
                    <TableCell>{dealer.tenant_count ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={dealer.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={dealer.is_active ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'none', md: 'table-cell' } }}>
                      {new Date(dealer.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dealers/${dealer.id}`);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Card>

      {/* Create Dealer Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Dealer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={createForm.name}
                onChange={handleCreateFormChange('name')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={createForm.email}
                onChange={handleCreateFormChange('email')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={createForm.password}
                onChange={handleCreateFormChange('password')}
                required
                helperText="Minimum 8 characters"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateDealer}
            disabled={createLoading}
          >
            {createLoading ? <CircularProgress size={20} /> : 'Add Dealer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
